import React, { useState } from 'react';
import { Plus, AlertTriangle, Clock, CheckCircle, X, Camera, FileText, Wrench } from 'lucide-react';

const normalizeEstimatedCost = (value) => {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  const trimmed = value.toString().trim();
  if (!trimmed) {
    return null;
  }

  const cleaned = trimmed.replace(/[^0-9.-]/g, '');
  if (!cleaned) {
    return null;
  }

  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : null;
};

const DefectReporting = ({ onDefectReport, existingDefects = [], photos = [] }) => {
  const [defects, setDefects] = useState(existingDefects);
  const [showForm, setShowForm] = useState(false);
  const [editingDefect, setEditingDefect] = useState(null);

  const severityLevels = [
    { id: 'critical', label: 'Critical', color: 'bg-red-100 text-red-800', description: 'Vehicle unsafe to operate' },
    { id: 'major', label: 'Major', color: 'bg-orange-100 text-orange-800', description: 'Requires immediate attention' },
    { id: 'minor', label: 'Minor', color: 'bg-yellow-100 text-yellow-800', description: 'Should be fixed soon' },
    { id: 'cosmetic', label: 'Cosmetic', color: 'bg-blue-100 text-blue-800', description: 'Appearance only' }
  ];

  const defectCategories = [
    'Engine/Powertrain',
    'Brakes',
    'Steering/Suspension',
    'Electrical',
    'Tires/Wheels',
    'Body/Exterior',
    'Interior',
    'Lights',
    'Safety Equipment',
    'Other'
  ];

  const statusOptions = [
    { id: 'reported', label: 'Reported', icon: AlertTriangle, color: 'text-red-600' },
    { id: 'in-progress', label: 'In Progress', icon: Clock, color: 'text-yellow-600' },
    { id: 'resolved', label: 'Resolved', icon: CheckCircle, color: 'text-green-600' }
  ];

  const DefectForm = ({ defect = null, onSubmit, onCancel }) => {
    const [formData, setFormData] = useState({
      id: defect?.id || `defect_${Date.now()}_${Math.random().toString(36).substr(2, 9)}_${performance.now()}`.replace(/\./g, ''),
      category: defect?.category || '',
      severity: defect?.severity || 'minor',
      title: defect?.title || '',
      description: defect?.description || '',
      location: defect?.location || '',
      photos: defect?.photos || [],
      status: defect?.status || 'reported',
      reportedAt: defect?.reportedAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      estimatedCost: defect?.estimatedCost ?? '',
      workOrder: defect?.workOrder || '',
      notes: defect?.notes || ''
    });

    const [errors, setErrors] = useState({});

    const validateForm = () => {
      const newErrors = {};
      if (!formData.category) newErrors.category = 'Category is required';
      if (!formData.title) newErrors.title = 'Title is required';
      if (!formData.description) newErrors.description = 'Description is required';
      if (!formData.location) newErrors.location = 'Location is required';
      
      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
      e.preventDefault();
      if (validateForm()) {
        const normalized = {
          ...formData,
          estimatedCost: normalizeEstimatedCost(formData.estimatedCost)
        };
        onSubmit(normalized);
      }
    };

    const handlePhotoSelect = (photoId) => {
      const photo = photos.find(p => p.id === photoId);
      if (photo && !formData.photos.find(p => p.id === photoId)) {
        setFormData(prev => ({
          ...prev,
          photos: [...prev.photos, photo]
        }));
      }
    };

    const removePhoto = (photoId) => {
      setFormData(prev => ({
        ...prev,
        photos: prev.photos.filter(p => p.id !== photoId)
      }));
    };

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className="bg-white rounded-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden">
          <div className="flex items-center justify-between p-4 border-b">
            <h3 className="text-lg font-semibold">
              {defect ? 'Edit Defect' : 'Report New Defect'}
            </h3>
            <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
              <X className="h-6 w-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-4 overflow-auto max-h-[calc(90vh-120px)]">
            <div className="space-y-4">
              {/* Category and Severity */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category *
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                    className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 ${
                      errors.category ? 'border-red-300' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select category...</option>
                    {defectCategories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                  {errors.category && <p className="text-red-600 text-xs mt-1">{errors.category}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Severity *
                  </label>
                  <select
                    value={formData.severity}
                    onChange={(e) => setFormData(prev => ({ ...prev, severity: e.target.value }))}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  >
                    {severityLevels.map(level => (
                      <option key={level.id} value={level.id}>
                        {level.label} - {level.description}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Brief description of the defect"
                  className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 ${
                    errors.title ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {errors.title && <p className="text-red-600 text-xs mt-1">{errors.title}</p>}
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description *
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Detailed description of the defect and symptoms"
                  rows={3}
                  className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 ${
                    errors.description ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {errors.description && <p className="text-red-600 text-xs mt-1">{errors.description}</p>}
              </div>

              {/* Location */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Location on Vehicle *
                </label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                  placeholder="e.g., Front left tire, Dashboard, Engine compartment"
                  className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 ${
                    errors.location ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {errors.location && <p className="text-red-600 text-xs mt-1">{errors.location}</p>}
              </div>

              {/* Photo Selection */}
              {photos.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Related Photos
                  </label>
                  <div className="grid grid-cols-3 gap-2 max-h-32 overflow-y-auto">
                    {photos.map((photo, index) => (
                      <div key={`${photo.id}_${index}`} className="relative">
                        <img
                          src={photo.preview}
                          alt="Available"
                          className="w-full h-16 object-cover rounded cursor-pointer border-2 border-transparent hover:border-blue-500"
                          onClick={() => handlePhotoSelect(photo.id)}
                        />
                        {formData.photos.find(p => p.id === photo.id) && (
                          <div className="absolute inset-0 bg-blue-600 bg-opacity-50 flex items-center justify-center rounded">
                            <CheckCircle className="h-6 w-6 text-white" />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  
                  {formData.photos.length > 0 && (
                    <div className="mt-2">
                      <p className="text-sm font-medium text-gray-700 mb-1">Selected Photos:</p>
                      <div className="flex flex-wrap gap-1">
                        {formData.photos.map((photo, index) => (
                          <span
                            key={`${photo.id}_${index}`}
                            className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                          >
                            {photo.name || 'Photo'}
                            <button
                              type="button"
                              onClick={() => removePhoto(photo.id)}
                              className="ml-1 text-blue-600 hover:text-blue-800"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Additional Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Estimated Cost
                  </label>
                  <input
                    type="text"
                    value={formData.estimatedCost}
                    onChange={(e) => setFormData(prev => ({ ...prev, estimatedCost: e.target.value }))}
                    placeholder="$0.00"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  >
                    {statusOptions.map(status => (
                      <option key={status.id} value={status.id}>{status.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Additional Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Any additional information or repair notes"
                  rows={2}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-2 pt-4 border-t mt-4">
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                {defect ? 'Update Defect' : 'Report Defect'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  const handleSubmitDefect = (defectData) => {
    const updatedDefects = editingDefect
      ? defects.map(d => d.id === editingDefect.id ? defectData : d)
      : [...defects, defectData];

    setDefects(updatedDefects);
    onDefectReport(updatedDefects);
    setShowForm(false);
    setEditingDefect(null);
  };

  const handleEditDefect = (defect) => {
    setEditingDefect(defect);
    setShowForm(true);
  };

  const handleDeleteDefect = (defectId) => {
    const updatedDefects = defects.filter(d => d.id !== defectId);
    setDefects(updatedDefects);
    onDefectReport(updatedDefects);
  };

  const getSeverityColor = (severity) => {
    return severityLevels.find(s => s.id === severity)?.color || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status) => {
    const statusOption = statusOptions.find(s => s.id === status);
    if (!statusOption) return AlertTriangle;
    return statusOption.icon;
  };

  const getStatusColor = (status) => {
    return statusOptions.find(s => s.id === status)?.color || 'text-gray-600';
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Defect Reporting</h2>
          <p className="text-sm text-gray-600 mt-1">
            {defects.length} defect{defects.length !== 1 ? 's' : ''} reported
          </p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
        >
          <Plus className="h-5 w-5 mr-2" />
          Report Defect
        </button>
      </div>

      {/* Defects List */}
      {defects.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Defects Reported</h3>
          <p className="text-gray-600">This vehicle appears to be in good condition.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {defects.map(defect => {
            const StatusIcon = getStatusIcon(defect.status);
            
            return (
              <div key={defect.id} className="bg-white rounded-lg shadow border-l-4 border-red-500">
                <div className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <h3 className="font-semibold text-gray-900">{defect.title}</h3>
                        <span className={`px-2 py-1 text-xs rounded-full ${getSeverityColor(defect.severity)}`}>
                          {severityLevels.find(s => s.id === defect.severity)?.label}
                        </span>
                        <div className={`flex items-center text-sm ${getStatusColor(defect.status)}`}>
                          <StatusIcon className="h-4 w-4 mr-1" />
                          {statusOptions.find(s => s.id === defect.status)?.label}
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{defect.description}</p>
                      <div className="flex items-center text-xs text-gray-500 space-x-4">
                        <span>📍 {defect.location}</span>
                        <span>📂 {defect.category}</span>
                        <span>📅 {new Date(defect.reportedAt).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <div className="flex space-x-2 ml-4">
                      <button
                        onClick={() => handleEditDefect(defect)}
                        className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                        title="Edit Defect"
                      >
                        <Wrench className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteDefect(defect.id)}
                        className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                        title="Delete Defect"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  {/* Photos */}
                  {defect.photos && defect.photos.length > 0 && (
                    <div className="mt-3">
                      <div className="flex items-center text-sm text-gray-600 mb-2">
                        <Camera className="h-4 w-4 mr-1" />
                        {defect.photos.length} photo{defect.photos.length !== 1 ? 's' : ''}
                      </div>
                      <div className="flex space-x-2 overflow-x-auto">
                        {defect.photos.map((photo, index) => (
                          <img
                            key={`${photo.id}_${index}`}
                            src={photo.preview}
                            alt="Defect"
                            className="h-16 w-16 object-cover rounded border flex-shrink-0"
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Additional Info */}
                  {(defect.estimatedCost || defect.workOrder || defect.notes) && (
                    <div className="mt-3 pt-3 border-t border-gray-200 text-sm">
                      {defect.estimatedCost && (
                        <p className="text-gray-600">
                          <strong>Estimated Cost:</strong> {defect.estimatedCost}
                        </p>
                      )}
                      {defect.workOrder && (
                        <p className="text-gray-600">
                          <strong>Work Order:</strong> {defect.workOrder}
                        </p>
                      )}
                      {defect.notes && (
                        <p className="text-gray-600">
                          <strong>Notes:</strong> {defect.notes}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Defect Form Modal */}
      {showForm && (
        <DefectForm
          defect={editingDefect}
          onSubmit={handleSubmitDefect}
          onCancel={() => {
            setShowForm(false);
            setEditingDefect(null);
          }}
        />
      )}
    </div>
  );
};

export default DefectReporting;
